import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { ELEVATOR_HISTORY_TAB } from '~/components/compliance/detailPage/ElevatorTabContainer';
import {
  CAR_PANEL_HISTORY_TAB,
  carPanelTabKey,
  selectedCarIdKey,
  selectedContractIdKey,
  selectedInvoiceIdKey,
  selectedTabSearchParam,
} from '~/components/global/constants';

/**
 * Invoices, contracts and elevators each open as their own page, but the entry points
 * that reach them — the weekly invoice email, chat cards, the command palette, links
 * built in column definitions — all still set a search param. The param stays the
 * single entry and is converted to a navigation here, so none of them needs to know
 * where the page lives.
 *
 * Mounted once in the app shell, so the conversion works from any route.
 *
 * The rest of the query string rides along: the global date range and filters live
 * there. The id param itself is dropped, or the page would redirect to itself forever.
 * `replace`, so Back skips the transient param URL and returns to the page the reader
 * came from.
 */
export function DetailParamRedirects() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const invoiceId = searchParams.get(selectedInvoiceIdKey);
    if (invoiceId) {
      const search = new URLSearchParams(searchParams);
      search.delete(selectedInvoiceIdKey);
      // `tab` is kept, so `?tab=dispute` still lands on the Dispute tab;
      // `InvoiceTabContainer` consumes it and clears it.
      //
      // No shim for the older `selectedTab` key, though it was a real param:
      // `65c1c593a` (2024-03-18) shipped `selectedTabSearchParam = 'selectedTab'` and
      // built links like `?selectedInvoiceId=<id>&selectedTab=CONTRACTS`, and
      // `9004f132f` (2024-12-06) renamed it to `tab`. Those links have been dead for 21
      // months, and their values were uppercase plurals (`CONTRACTS`) that match no
      // current tab label (`contract`), so honouring the key would not revive them.
      void navigate(
        { pathname: `/spending/${invoiceId}`, search: search.toString() },
        { replace: true },
      );
      return;
    }

    const contractId = searchParams.get(selectedContractIdKey);
    if (contractId) {
      const search = new URLSearchParams(searchParams);
      search.delete(selectedContractIdKey);
      void navigate(
        { pathname: `/contracts/${contractId}`, search: search.toString() },
        { replace: true },
      );
      return;
    }

    const carId = searchParams.get(selectedCarIdKey);
    if (carId) {
      const search = new URLSearchParams(searchParams);
      search.delete(selectedCarIdKey);
      // The old panel's tab key becomes the page's. Nothing in the app writes
      // `carPanelTab` any more — the property grid's name cell links straight to the
      // elevator page — so this branch now serves only bookmarks and links saved before
      // the panel became a page. Those still need it.
      if (search.get(carPanelTabKey) === CAR_PANEL_HISTORY_TAB) {
        search.set(selectedTabSearchParam, ELEVATOR_HISTORY_TAB);
      }
      search.delete(carPanelTabKey);
      void navigate(
        { pathname: `/compliance/${carId}`, search: search.toString() },
        { replace: true },
      );
    }
  }, [searchParams, navigate]);

  return null;
}
